import { useState, useEffect, useId } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SectionHeading } from "@/components/Atoms";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PackOpeningModal } from "@/components/PackOpeningModal";
import { SkillTreeMap } from "@/components/SkillTreeMap";
import { TinyCard } from "@/components/Card3D";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Coins, Star, Gem, Lock, Check, Gift, Loader2, Sparkles, Trophy,
  Hammer, Dices, Package, Clock,
} from "lucide-react";

const TIER_COLORS = {
  apprentice: "#34d399", journeyman: "#38bdf8", master: "#a78bfa", grandmaster: "#fbbf24",
};

const RARITY_COLORS = {
  common: "#9ca3af", uncommon: "#34d399", rare: "#38bdf8",
  epic: "#a78bfa", legendary: "#fbbf24", goddess: "#f472b6",
};

/* ── Reward chips ──────────────────────────────────────────────────────── */
function RewardChips({ rewards }) {
  const rewardId = useId().replace(/[^a-zA-Z0-9-]/g, "");
  if (!rewards) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {rewards.credits > 0 && (
        <span className="inline-flex items-center gap-1 rounded-md bg-amber-400/10 border border-amber-400/30 px-2 py-0.5 text-[11px] text-amber-300 font-mono">
          <Coins className="h-3 w-3" /> {rewards.credits.toLocaleString()}
        </span>
      )}
      {rewards.sp > 0 && (
        <span className="inline-flex items-center gap-1 rounded-md bg-yellow-300/10 border border-yellow-300/30 px-2 py-0.5 text-[11px] text-yellow-200 font-mono">
          <Star className="h-3 w-3" /> {rewards.sp} SP
        </span>
      )}
      {rewards.card && (
        <span className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-mono uppercase"
              style={{ color: RARITY_COLORS[rewards.card] || "#fff", background: `${RARITY_COLORS[rewards.card] || "#888"}1f`, border: `1px solid ${RARITY_COLORS[rewards.card] || "#888"}59` }}>
          <Gem className="h-3 w-3" /> {rewards.card} card
        </span>
      )}
      {rewards.pack && (
        <span className="inline-flex items-center gap-1 rounded-md bg-fuchsia-400/10 border border-fuchsia-400/40 px-2 py-0.5 text-[11px] text-fuchsia-300 font-mono uppercase">
          <Package className="h-3 w-3" /> {rewards.pack_name || "card pack"}
        </span>
      )}
      {rewards.token && (
        <span data-testid={`reward-token-${rewardId}`} className="inline-flex items-center gap-1 rounded-md bg-orange-400/10 border border-orange-400/40 px-2 py-0.5 text-[11px] text-orange-300 font-mono uppercase">
          <Gift className="h-3 w-3" /> {rewards.token_qty || 1}× {({ reroll_token: "Reroll Token", hq_enhancement: "HQ token", talentshow_free_entry: "Show Pass", arena_token: "Arena Token", pack_bonus_card: "Bountiful Charm" })[rewards.token] || rewards.token}
        </span>
      )}
      {rewards.value > 0 && (
        <span data-testid={`reward-value-${rewardId}`} className="inline-flex items-center gap-1 rounded-md bg-white/5 border border-white/10 px-2 py-0.5 text-[10px] text-white/45 font-mono">
          ≈ {rewards.value.toLocaleString()}c value
        </span>
      )}
    </div>
  );
}

/* ── A single quest row ────────────────────────────────────────────────── */
function QuestCard({ q, onClaim, claiming }) {
  const pct = Math.min(100, Math.round((q.progress / q.target) * 100));
  const done = q.status === "claimable";
  const claimed = q.status === "claimed";
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      data-testid={`quest-card-${q.quest_id}`}
      className={`rounded-xl border p-4 transition-colors ${done ? "border-amber-400/40 bg-amber-400/[0.05]" : claimed ? "border-white/5 bg-white/[0.02] opacity-60" : "border-white/10 bg-white/[0.02]"}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-white font-semibold text-sm flex items-center gap-2">{q.title}{claimed && <Check className="h-3.5 w-3.5 text-emerald-400" />}</div>
          <div className="text-white/50 text-xs mt-0.5">{q.desc}</div>
        </div>
        <Button
          size="sm"
          data-testid={`quest-claim-${q.quest_id}`}
          disabled={!done || claiming}
          onClick={() => onClaim(q)}
          className={done ? "bg-amber-400 text-black hover:bg-amber-300 shrink-0" : "bg-white/5 text-white/40 shrink-0"}
        >
          {claiming ? <Loader2 className="h-4 w-4 animate-spin" /> : claimed ? "Claimed" : done ? "Claim" : "In progress"}
        </Button>
      </div>
      {!claimed && (
        <div className="mt-3">
          <div className="h-2 rounded-full bg-black/50 overflow-hidden border border-white/5">
            <div className="h-full bg-gradient-to-r from-amber-400 to-orange-400 transition-all duration-500" style={{ width: `${pct}%` }} />
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="font-mono text-[10px] text-white/40">{q.progress} / {q.target}</span>
            <RewardChips rewards={q.rewards} />
          </div>
        </div>
      )}
    </motion.div>
  );
}

/* ── Commission Board ──────────────────────────────────────────────────── */
function useCountdown(deadline) {
  const [secs, setSecs] = useState(null);
  useEffect(() => {
    if (!deadline) { setSecs(null); return; }
    const tick = () => setSecs(Math.max(0, Math.floor((new Date(deadline).getTime() - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [deadline]);
  return secs;
}

function fmtDur(secs) {
  if (secs == null) return "";
  const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), s = secs % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function CommissionQuestCard({ q, onClaim, claiming }) {
  const pct = Math.min(100, Math.round((q.progress / q.target) * 100));
  const done = q.status === "claimable";
  const failed = q.status === "failed";
  const tc = TIER_COLORS[q.tier] || "#22d3ee";
  const timed = q.pay_with === "card" && q.deadline;
  const secsLeft = useCountdown(timed && !done && !failed ? q.deadline : null);
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      data-testid={`commission-quest-${q.id}`}
      className={`rounded-xl border p-4 ${failed ? "border-rose-500/40 bg-rose-500/[0.05]" : done ? "border-amber-400/40 bg-amber-400/[0.05]" : "border-white/10 bg-white/[0.02]"}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[8px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded" style={{ color: tc, background: `${tc}1f`, border: `1px solid ${tc}40` }}>{q.tier_label}</span>
            <span className="text-white font-semibold text-sm">{q.title}</span>
            {q.doubled && <span className="font-mono text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-400/15 text-amber-300 border border-amber-400/40" data-testid={`commission-doubled-${q.id}`}>2×</span>}
            {timed && !failed && (
              <span data-testid={`commission-timer-${q.id}`} className={`inline-flex items-center gap-1 font-mono text-[9px] font-bold px-1.5 py-0.5 rounded ${secsLeft != null && secsLeft < 3600 ? "bg-rose-500/20 text-rose-300 border border-rose-400/40" : "bg-violet-500/15 text-violet-300 border border-violet-400/30"}`}>
                <Clock className="h-3 w-3" /> {done ? "in time" : fmtDur(secsLeft)}
              </span>
            )}
          </div>
          <div className="text-white/50 text-xs mt-0.5">{q.desc}</div>
        </div>
        <Button size="sm" data-testid={`commission-claim-${q.id}`} disabled={!done || claiming} onClick={() => onClaim(q)}
          className={done ? "bg-amber-400 text-black hover:bg-amber-300 shrink-0" : "bg-white/5 text-white/40 shrink-0"}>
          {claiming ? <Loader2 className="h-4 w-4 animate-spin" /> : done ? "Claim" : failed ? "Failed" : "In progress"}
        </Button>
      </div>
      {q.staked_card && (
        <div className="mt-2.5 flex items-center gap-2 rounded-lg bg-black/30 border border-white/8 px-2 py-1.5" data-testid={`commission-staked-${q.id}`}>
          <div className={`h-9 w-7 rounded overflow-hidden bg-black shrink-0 rarity-${q.staked_card.rarity}`}>
            {q.staked_card.front_image && <img src={q.staked_card.front_image} alt="" className={`w-full h-full object-cover ${failed ? "grayscale" : ""}`} />}
          </div>
          <div className="min-w-0 text-[11px]">
            <div className="text-white/80 truncate">Staked: <span className="font-medium">{q.staked_card.title}</span></div>
            <div className={`font-mono ${failed ? "text-rose-400" : "text-white/45"}`}>{failed ? "Forfeited to the house" : "Locked — finish in time to keep it"}</div>
          </div>
        </div>
      )}
      <div className="mt-3">
        <div className="h-2 rounded-full bg-black/50 overflow-hidden border border-white/5">
          <div className="h-full transition-all duration-500" style={{ width: `${failed ? 100 : pct}%`, background: failed ? "#f43f5e" : `linear-gradient(90deg, ${tc}, #fb923c)` }} />
        </div>
        <div className="flex items-center justify-between mt-2">
          <span className="font-mono text-[10px] text-white/40">{q.progress} / {q.target}</span>
          <RewardChips rewards={q.rewards} />
        </div>
      </div>
    </motion.div>
  );
}

/* Pick one owned card to stake on a timed commission */
function StakeCardDialog({ open, onClose, onPick, picking }) {
  const { data: cards } = useQuery({ queryKey: ["my-cards"], queryFn: async () => (await api.get("/cards/mine")).data, enabled: open });
  const available = (cards || []).filter((c) => !c.locked && !c.locked_in_commission && !c.locked_in_cavern && !c.locked_in_market && !c.locked_in_trade && !c.locked_in_battle && !c.locked_in_show && !c.locked_in_request);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[hsl(222_22%_8%)] border-white/10 max-w-lg max-h-[85vh] overflow-y-auto" data-testid="commission-stake-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl text-white tracking-[0.06em]">Stake a card</DialogTitle></DialogHeader>
        <DialogDescription className="text-white/50 text-xs">
          The card you stake is <span className="text-rose-300">locked and at risk</span>. Finish the objective before the timer runs out to keep it and claim your reward — fail, and it's forfeited to the house.
        </DialogDescription>
        {available.length === 0 ? (
          <div className="py-8 text-center text-white/40 text-sm">No free cards to stake.</div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5 mt-1">
            {available.slice(0, 40).map((c) => (
              <div key={c.id} data-testid={`stake-card-${c.id}`}>
                <TinyCard card={c} idKey={c.id} onClick={() => onPick(c.id)}
                  footer={<div className="text-white/70 text-[10px] truncate mt-0.5">{c.title}</div>} />
              </div>
            ))}
          </div>
        )}
        {picking && <div className="text-center text-white/50 text-xs mt-2"><Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Staking…</div>}
      </DialogContent>
    </Dialog>
  );
}

/* Packs won from commissions, waiting to be opened */
function WonPacks() {
  const qc = useQueryClient();
  const [opening, setOpening] = useState(null);
  const { data: packs } = useQuery({ queryKey: ["pack-inventory"], queryFn: async () => (await api.get("/packs/inventory")).data, refetchInterval: 15000 });
  const open = useMutation({
    mutationFn: async (invId) => (await api.post(`/packs/inventory/${invId}/open`)).data,
    onSuccess: (d) => { setOpening({ cards: d.cards, packName: d.pack_name }); qc.invalidateQueries({ queryKey: ["pack-inventory"] }); qc.invalidateQueries({ queryKey: ["my-cards"] }); },
    onError: (e) => toast.error(apiError(e, "Couldn't open pack")),
  });
  if (!packs || packs.length === 0) return null;
  return (
    <section data-testid="won-packs">
      <h3 className="font-mono text-[11px] uppercase tracking-[0.3em] text-fuchsia-300/80 mb-3 flex items-center gap-1.5"><Package className="h-3.5 w-3.5" /> Packs Won — ready to open</h3>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {packs.map((p) => (
          <div key={p.id} data-testid={`won-pack-${p.id}`} className="rounded-xl border border-fuchsia-400/30 bg-fuchsia-400/[0.05] p-3 flex items-center gap-3">
            <div className="h-12 w-12 rounded-lg bg-fuchsia-500/15 border border-fuchsia-400/30 flex items-center justify-center shrink-0 overflow-hidden">
              {p.pack_image ? <img src={p.pack_image} alt="" className="w-full h-full object-cover" /> : <Package className="h-6 w-6 text-fuchsia-300" />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-white text-sm font-medium truncate">{p.pack_name || "Card Pack"}</div>
              <div className="font-mono text-[10px] text-white/40">Won from a commission</div>
            </div>
            <Button size="sm" data-testid={`open-won-pack-${p.id}`} disabled={open.isPending} onClick={() => open.mutate(p.id)} className="bg-fuchsia-400 text-black hover:bg-fuchsia-300 shrink-0">Open</Button>
          </div>
        ))}
      </div>
      {opening && <PackOpeningModal cards={opening.cards} packName={opening.packName} onClose={() => setOpening(null)} />}
    </section>
  );
}

function CommissionBoard({ commissions, onClaim, claiming }) {
  const qc = useQueryClient();
  const { refresh } = useAuth();
  const [offering, setOffering] = useState(500);
  const [roll, setRoll] = useState(null);
  const [doubled, setDoubled] = useState({});
  const [payWith, setPayWith] = useState({}); // option_id -> 'gold'|'sp'|'card'
  const [stakeFor, setStakeFor] = useState(null); // {option_id, double} awaiting a card pick
  const [filter, setFilter] = useState("all"); // all | active | claimable | failed
  const [cpage, setCpage] = useState(0);
  const C_PER_PAGE = 6;

  const doRoll = useMutation({
    mutationFn: async () => (await api.post("/progression/commissions/roll", { offering: Number(offering) })).data,
    onSuccess: (d) => { setRoll(d); setDoubled({}); setPayWith({}); },
    onError: (e) => toast.error(apiError(e, "Couldn't roll offers")),
  });
  const accept = useMutation({
    mutationFn: async ({ option_id, double, pay_with, staked_card_id }) =>
      (await api.post("/progression/commissions/accept", { roll_id: roll.roll_id, option_id, double, pay_with, staked_card_id })).data,
    onSuccess: () => {
      toast.success("Commission added to your Path!");
      setRoll(null); setDoubled({}); setPayWith({}); setStakeFor(null);
      qc.invalidateQueries({ queryKey: ["progression"] });
      qc.invalidateQueries({ queryKey: ["my-cards"] });
      refresh();
    },
    onError: (e) => toast.error(apiError(e, "Couldn't commission")),
  });

  const onCommission = (o, isDbl) => {
    const method = payWith[o.option_id] || "gold";
    if (method === "card") { setStakeFor({ option_id: o.option_id, double: isDbl }); return; }
    accept.mutate({ option_id: o.option_id, double: isDbl, pay_with: method });
  };

  const PAY_METHODS = [
    { key: "gold", label: "Gold", icon: Coins },
    { key: "sp", label: "SP", icon: Star },
    { key: "card", label: "Stake", icon: Lock },
  ];

  return (
    <div className="space-y-8" data-testid="commission-board">
      <WonPacks />
      <section className="rounded-2xl border border-white/10 bg-gradient-to-br from-amber-400/[0.06] to-fuchsia-400/[0.03] p-5">
        <div className="flex items-center gap-2 mb-1">
          <Hammer className="h-4 w-4 text-amber-300" />
          <h3 className="font-display text-lg text-white tracking-wide">Commission a Quest</h3>
        </div>
        <p className="text-white/50 text-xs mb-4 max-w-2xl">
          Set an offering, then pick one of four contracts. Pay with <span className="text-amber-300">gold</span>, <span className="text-yellow-200">Skill Points</span>, or <span className="text-violet-300">stake a card on a timer</span> — and win SP, cards or a sealed card pack. Each tile rolls within ±35% of your bid.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Your offering (value)</Label>
            <Input data-testid="commission-offering-input" type="number" min={100} max={5000} value={offering}
              onChange={(e) => setOffering(e.target.value)} className="mt-1 w-44 bg-[hsl(220_18%_12%)] border-white/10" />
          </div>
          <Button data-testid="commission-roll-button" onClick={() => doRoll.mutate()} disabled={doRoll.isPending}
            className="bg-amber-400 text-black hover:bg-amber-300">
            {doRoll.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Dices className="h-4 w-4 mr-1.5" /> Get Offers</>}
          </Button>
          <span className="font-mono text-[10px] text-white/35 pb-2">100 – 5,000</span>
        </div>

        {roll && (
          <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="commission-offers">
            {roll.options.map((o) => {
              const tc = TIER_COLORS[o.tier] || "#22d3ee";
              const isDbl = !!doubled[o.option_id];
              const v = isDbl ? o.double : o;
              const pct = o.reward_pct ?? 0;
              const pctColor = pct >= 0 ? "#34d399" : "#fb7185";
              const method = payWith[o.option_id] || "gold";
              return (
                <div key={o.option_id} data-testid={`commission-tile-${o.tier}`}
                  className="rounded-xl border p-3 flex flex-col" style={{ borderColor: `${tc}40`, background: `${tc}0d` }}>
                  <div className="flex items-center justify-between gap-1 mb-2">
                    <span className="font-mono text-[8px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded"
                      style={{ color: tc, background: `${tc}1f`, border: `1px solid ${tc}40` }}>{o.tier_label}</span>
                    <span className="font-mono text-[10px] font-bold" data-testid={`commission-pct-${o.tier}`}
                      title="How far the reward lands from your offering" style={{ color: pctColor }}>
                      {pct >= 0 ? `+${pct}` : pct}% vs offer
                    </span>
                  </div>
                  <div className="text-white font-semibold text-sm">{o.title}</div>
                  <div className="text-white/55 text-xs mt-0.5 flex-1">{v.desc}</div>

                  {/* Reward */}
                  <div className="mt-2">
                    <div className="font-mono text-[8px] uppercase tracking-[0.2em] text-white/35 mb-1">Reward</div>
                    <RewardChips rewards={v.rewards} />
                  </div>

                  {/* Payment method picker */}
                  <div className="mt-2.5">
                    <div className="font-mono text-[8px] uppercase tracking-[0.2em] text-white/35 mb-1">Pay with</div>
                    <div className="grid grid-cols-3 gap-1">
                      {PAY_METHODS.map((m) => {
                        const active = method === m.key;
                        return (
                          <button key={m.key} type="button" data-testid={`commission-pay-${o.tier}-${m.key}`}
                            onClick={() => setPayWith((p) => ({ ...p, [o.option_id]: m.key }))}
                            className={`flex flex-col items-center gap-0.5 rounded-md border py-1 transition-colors ${active ? "border-white/60 bg-white/10" : "border-white/10 bg-white/[0.02] hover:border-white/25"}`}>
                            <m.icon className={`h-3 w-3 ${active ? "text-white" : "text-white/45"}`} />
                            <span className={`font-mono text-[8px] ${active ? "text-white" : "text-white/45"}`}>{m.label}</span>
                          </button>
                        );
                      })}
                    </div>
                    <div className="mt-1.5 font-mono text-[10px] text-center" data-testid={`commission-cost-${o.tier}`}>
                      {method === "gold" && <span className="inline-flex items-center gap-1 text-amber-300"><Coins className="h-3 w-3" />{v.cost.toLocaleString()}</span>}
                      {method === "sp" && <span className="inline-flex items-center gap-1 text-yellow-200"><Star className="h-3 w-3" />{v.cost_sp} SP</span>}
                      {method === "card" && <span className="inline-flex items-center gap-1 text-violet-300"><Clock className="h-3 w-3" />Timed · {v.stake_minutes >= 60 ? `${Math.floor(v.stake_minutes / 60)}h ${v.stake_minutes % 60}m` : `${v.stake_minutes}m`} to finish</span>}
                    </div>
                  </div>

                  {/* Pay double → do double → get double */}
                  <button type="button" data-testid={`commission-double-${o.tier}`}
                    onClick={() => setDoubled((p) => ({ ...p, [o.option_id]: !p[o.option_id] }))}
                    className={`mt-2.5 w-full rounded-lg border px-2 py-1.5 text-left transition-colors ${isDbl ? "border-amber-400/70 bg-amber-400/15" : "border-white/10 bg-white/[0.02] hover:border-white/25"}`}>
                    <div className="flex items-center justify-between">
                      <span className={`font-display text-sm ${isDbl ? "text-amber-200" : "text-white/80"}`}>2× Double</span>
                      <span className={`relative h-4 w-7 rounded-full transition-colors ${isDbl ? "bg-amber-400" : "bg-white/15"}`}>
                        <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-black transition-all ${isDbl ? "left-3.5" : "left-0.5"}`} />
                      </span>
                    </div>
                    <div className="font-mono text-[9px] text-white/40 leading-tight mt-0.5">pay double · do double · get double</div>
                  </button>
                  <Button size="sm" data-testid={`commission-accept-${o.tier}`} onClick={() => onCommission(o, isDbl)} disabled={accept.isPending}
                    className="mt-2.5 w-full font-semibold" style={{ background: tc, color: "#000" }}>
                    {accept.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : method === "card" ? "Stake & Commission" : isDbl ? "Commission ×2" : "Commission"}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <StakeCardDialog open={!!stakeFor} picking={accept.isPending} onClose={() => setStakeFor(null)}
        onPick={(cardId) => accept.mutate({ option_id: stakeFor.option_id, double: stakeFor.double, pay_with: "card", staked_card_id: cardId })} />

      <section data-testid="commission-active">
        {(() => {
          const FILTERS = [
            { k: "all", label: "All" },
            { k: "claimable", label: "Ready" },
            { k: "active", label: "Active" },
            { k: "failed", label: "Failed" },
          ];
          const counts = commissions.reduce((a, q) => { a[q.status] = (a[q.status] || 0) + 1; return a; }, {});
          const filtered = filter === "all" ? commissions : commissions.filter((q) => q.status === filter);
          const pages = Math.max(1, Math.ceil(filtered.length / C_PER_PAGE));
          const sp = Math.min(cpage, pages - 1);
          const shown = filtered.slice(sp * C_PER_PAGE, sp * C_PER_PAGE + C_PER_PAGE);
          return (
            <>
              <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                <h3 className="font-mono text-[11px] uppercase tracking-[0.3em] text-amber-300/80">Your Commissioned Quests</h3>
                <div className="flex gap-1" data-testid="commission-filter">
                  {FILTERS.map((f) => (
                    <button key={f.k} type="button" data-testid={`commission-filter-${f.k}`}
                      onClick={() => { setFilter(f.k); setCpage(0); }}
                      className={`px-2.5 py-1 rounded-md font-mono text-[10px] uppercase tracking-wider transition-colors ${filter === f.k ? "bg-amber-400 text-black" : "bg-white/5 text-white/55 hover:bg-white/10"}`}>
                      {f.label}{f.k !== "all" && counts[f.k] ? ` ${counts[f.k]}` : ""}
                    </button>
                  ))}
                </div>
              </div>
              {commissions.length === 0 ? (
                <div className="rounded-xl border border-dashed border-white/12 p-6 text-center text-white/40 text-sm" data-testid="commission-empty">
                  No commissioned quests yet — lay down an offering above to commission one.
                </div>
              ) : filtered.length === 0 ? (
                <div className="rounded-xl border border-dashed border-white/12 p-6 text-center text-white/40 text-sm" data-testid="commission-filter-empty">
                  No {filter} commissions.
                </div>
              ) : (
                <>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {shown.map((q) => <CommissionQuestCard key={q.id} q={q} onClaim={onClaim} claiming={claiming} />)}
                  </div>
                  {pages > 1 && (
                    <div className="flex items-center justify-center gap-3 mt-4" data-testid="commission-pagination">
                      <Button size="sm" variant="outline" data-testid="commission-prev-page" disabled={sp === 0} onClick={() => setCpage(sp - 1)} className="border-white/15 text-white/70 h-7">Prev</Button>
                      <span className="font-mono text-[11px] text-white/45">Page {sp + 1} / {pages}</span>
                      <Button size="sm" variant="outline" data-testid="commission-next-page" disabled={sp >= pages - 1} onClick={() => setCpage(sp + 1)} className="border-white/15 text-white/70 h-7">Next</Button>
                    </div>
                  )}
                </>
              )}
            </>
          );
        })()}
      </section>
    </div>
  );
}

export default function PathPage() {
  const qc = useQueryClient();
  const { refresh } = useAuth();
  const { data, isLoading } = useQuery({ queryKey: ["progression"], queryFn: async () => (await api.get("/progression")).data });

  const claim = useMutation({
    mutationFn: async (q) => (await api.post(`/progression/quests/${q.id}/claim`)).data,
    onSuccess: (res) => {
      const g = res.granted || {};
      const parts = [];
      if (g.credits) parts.push(`${g.credits.toLocaleString()} credits`);
      if (g.sp) parts.push(`${g.sp} SP`);
      if (g.card?.title) parts.push(`a ${g.card.rarity} card: ${g.card.title}`);
      else if (g.card?.credits_instead) parts.push(`${g.card.credits_instead} credits`);
      if (g.pack?.pack_name) parts.push(`a pack: ${g.pack.pack_name}`);
      if (g.card_returned?.title) parts.push(`${g.card_returned.title} returned`);
      toast.success(`Reward claimed${parts.length ? " — " + parts.join(", ") : ""}!`);
      qc.invalidateQueries({ queryKey: ["progression"] });
      refresh();
    },
    onError: (e) => toast.error(apiError(e, "Couldn't claim")),
  });

  const unlock = useMutation({
    mutationFn: async ({ branch, cell }) => (await api.post(`/progression/skilltree/unlock`, { branch, cell })).data,
    onSuccess: (res) => {
      const g = res.granted || {};
      const parts = [];
      if (g.card?.title) parts.push(`a ${g.card.rarity} card: ${g.card.title}`);
      else if (g.card?.credits_instead) parts.push(`${g.card.credits_instead.toLocaleString()} credits`);
      if (g.gear?.name) parts.push(`${g.gear.rarity} gear: ${g.gear.name}`);
      if (g.pack?.pack_name) parts.push(`a pack: ${g.pack.pack_name}`);
      if (g.buff === "extra_daily_quest") parts.push("+1 daily quest slot");
      else if (g.buff === "quest_credit_boost") parts.push(`+${g.pct}% quest credits`);
      toast.success(`Node unlocked${parts.length ? " — " + parts.join(", ") : ""}!`);
      qc.invalidateQueries({ queryKey: ["progression"] });
      refresh();
    },
    onError: (e) => toast.error(apiError(e, "Couldn't unlock")),
  });

  if (isLoading || !data) {
    return <div className="max-w-6xl mx-auto px-4 py-20 text-center text-white/50"><Loader2 className="h-8 w-8 animate-spin mx-auto" /></div>;
  }

  const { quests, sp, sp_total_earned, branches, perks } = data;
  const commissions = quests.commission || [];
  const commissionClaimable = commissions.filter((q) => q.status === "claimable").length;
  // Quests-tab badge counts only the quests rendered on this tab (not commissions,
  // which live on their own tab) so the badge never shows "nothing to do" here.
  const totalClaimable = ["starter", "daily", "weekly"].reduce(
    (n, k) => n + (quests[k] || []).filter((q) => q.status === "claimable").length, 0);

  return (
    <div className="max-w-6xl mx-auto px-3 sm:px-6 py-5 sm:py-10" data-testid="path-page">
      <SectionHeading
        eyebrow="Progression"
        title="THE ASCENDANT'S PATH"
        description="Complete quests to earn Skill Points, then carve your path through the tree to claim credits, cards and powerful capstones."
        right={
          <div className="hud-pill rounded-xl px-4 py-2 text-right" data-testid="sp-balance">
            <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/40">Skill Points</div>
            <div className="font-display text-2xl text-yellow-300 flex items-center gap-1.5 justify-end"><Star className="h-4 w-4" /> {sp}</div>
            <div className="text-[10px] text-white/35">{sp_total_earned} earned all-time</div>
          </div>
        }
      />

      <Tabs defaultValue="quests" className="w-full">
        <TabsList className="bg-white/5 border border-white/10">
          <TabsTrigger value="quests" data-testid="tab-quests" className="data-[state=active]:bg-amber-400 data-[state=active]:text-black">
            Quests {totalClaimable > 0 && <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold">{totalClaimable}</span>}
          </TabsTrigger>
          <TabsTrigger value="tree" data-testid="tab-tree" className="data-[state=active]:bg-amber-400 data-[state=active]:text-black">Skill Tree</TabsTrigger>
          <TabsTrigger value="commissions" data-testid="tab-commissions" className="data-[state=active]:bg-amber-400 data-[state=active]:text-black">
            Commissions {commissionClaimable > 0 && <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold">{commissionClaimable}</span>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="quests" className="mt-6 space-y-8">
          {quests.starter?.length > 0 && (
            <section data-testid="quests-starter">
              <h3 className="font-mono text-[11px] uppercase tracking-[0.3em] text-cyan-300/80 mb-3 flex items-center gap-2"><Gift className="h-3.5 w-3.5" /> Starter Path</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {quests.starter.map((q) => <QuestCard key={q.id} q={q} onClaim={(x) => claim.mutate(x)} claiming={claim.isPending} />)}
              </div>
            </section>
          )}
          <section data-testid="quests-daily">
            <h3 className="font-mono text-[11px] uppercase tracking-[0.3em] text-emerald-300/80 mb-3">Daily Quests</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {quests.daily.map((q) => <QuestCard key={q.id} q={q} onClaim={(x) => claim.mutate(x)} claiming={claim.isPending} />)}
            </div>
          </section>
          <section data-testid="quests-weekly">
            <h3 className="font-mono text-[11px] uppercase tracking-[0.3em] text-fuchsia-300/80 mb-3">Weekly Quests</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {quests.weekly.map((q) => <QuestCard key={q.id} q={q} onClaim={(x) => claim.mutate(x)} claiming={claim.isPending} />)}
            </div>
          </section>
        </TabsContent>

        <TabsContent value="tree" className="mt-6">
          {(perks?.quest_credit_boost > 0 || perks?.extra_daily_quest > 0) && (
            <div className="text-center text-[11px] text-emerald-300/80 mb-3" data-testid="active-perks">
              Active perks: {perks.quest_credit_boost > 0 && `+${Math.round(perks.quest_credit_boost * 100)}% quest credits`}{perks.quest_credit_boost > 0 && perks.extra_daily_quest && " · "}{perks.extra_daily_quest && "+1 daily quest"}
            </div>
          )}
          <SkillTreeMap branches={branches} onUnlock={(n) => unlock.mutate(n)} unlocking={unlock.isPending} />
        </TabsContent>

        <TabsContent value="commissions" className="mt-6">
          <CommissionBoard commissions={commissions} onClaim={(x) => claim.mutate(x)} claiming={claim.isPending} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
